import {
  faPen,
  faPlus,
  faSave,
  faTimes,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import EmptyList from 'components/common/EmptyList';
import Flex from 'components/common/Flex';
import SearchInputWithSuggestions from 'components/common/SearchInputWithSuggestions';
import { formatDateTime, useDebounce } from 'helpers/utils';
import useAdvanceTable from 'hooks/useAdvanceTable';
import useNotification from 'hooks/useNotification';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { useEffect, useRef, useState } from 'react';
import {
  Badge,
  Button,
  ButtonGroup,
  Col,
  Form,
  Row,
  Spinner
} from 'react-bootstrap';
import CurrencyInput from 'react-currency-input-field';
import ProductServiceService from 'services/ProductService.service';
import TicketBillableItemService from 'services/TicketBillableItem.service';
import TicketBillableItemDeleteModal from './TicketBillableItemDeleteModal';

const TicketBillableItems = ({
  ticketId,
  ticketStatus,
  refreshLogs,
  onCountChange
}) => {
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);

  const [items, setItems] = useState({
    content: [],
    totalElements: 0
  });

  // Estados para criação/edição inline
  const [productServices, setProductServices] = useState([]);
  const [selectedProductServiceId, setSelectedProductServiceId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const isSelectingProductRef = useRef(false);
  const searchInputContainerRef = useRef(null);
  const searchInputRef = useRef(null);
  const blurTimeoutRef = useRef(null);
  const wasFocusedRef = useRef(false);
  const [quantity, setQuantity] = useState(1);
  const [customUnitPrice, setCustomUnitPrice] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);

  const [filters] = useState({
    page: 0,
    size: 100
  });

  const getData = async () => {
    setIsLoading(true);
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const response = await TicketBillableItemService.listByTicket(
        ticketId,
        queryParams
      );
      if (response.content) {
        setItems(response);
        if (onCountChange) {
          onCountChange(response.content.length);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar itens faturáveis:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (ticketId) {
      getData();
    }
  }, [ticketId]);

  // Cleanup do timeout de blur ao desmontar o componente
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  const loadProductServices = async searchValue => {
    // Verificar se o input estava focado antes do loading
    wasFocusedRef.current =
      searchInputRef.current &&
      document.activeElement === searchInputRef.current;

    setIsLoadingProducts(true);
    try {
      const params = {
        page: 0,
        size: 1000,
        inactive: false
      };

      if (searchValue && searchValue.trim()) {
        params.searchTerm = searchValue.trim();
      }

      const queryParams = new URLSearchParams(params).toString();
      const response = await ProductServiceService.list(queryParams);
      if (response.content) {
        setProductServices(response.content);
      }
    } catch (error) {
      console.error('Erro ao buscar produtos/serviços:', error);
    } finally {
      setIsLoadingProducts(false);
      // Restaurar o foco se estava focado antes do loading
      if (wasFocusedRef.current && searchInputRef.current) {
        setTimeout(() => {
          searchInputRef.current?.focus();
          wasFocusedRef.current = false;
        }, 0);
      }
    }
  };

  // Debounce para busca
  const debouncedLoadProductServices = useDebounce(searchValue => {
    loadProductServices(searchValue);
  }, 300);

  // Buscar produtos quando o termo de busca mudar (mas não quando selecionar um produto)
  useEffect(() => {
    // Não fazer busca se estivermos selecionando um produto
    if (isSelectingProductRef.current) {
      isSelectingProductRef.current = false;
      return;
    }

    // Só fazer busca se o usuário digitar algo
    if (searchTerm.trim()) {
      debouncedLoadProductServices(searchTerm);
    } else {
      // Se o campo estiver vazio, limpar a lista de produtos
      setProductServices([]);
    }
  }, [searchTerm]);

  const handleSearchChange = e => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowSuggestions(true);
    // Limpar timeout anterior se existir
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    // Garantir que o foco permaneça no input
    if (e.target && document.activeElement !== e.target) {
      e.target.focus();
    }
  };

  const handleBlur = e => {
    // Limpar timeout anterior se existir
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }

    // Usar setTimeout para verificar o elemento ativo após o blur ser processado
    blurTimeoutRef.current = setTimeout(() => {
      // Verificar se o foco realmente saiu do container (input + dropdown)
      const activeElement = document.activeElement;
      if (
        searchInputContainerRef.current &&
        activeElement &&
        !searchInputContainerRef.current.contains(activeElement)
      ) {
        setShowSuggestions(false);
      }
      blurTimeoutRef.current = null;
    }, 150);
  };

  const handleProductServiceSelect = product => {
    isSelectingProductRef.current = true;
    setSelectedProductServiceId(String(product.id));
    const productName = product.name || '';
    setSearchTerm(productName);
    setCustomUnitPrice(product.unitPrice || 0);
    setShowSuggestions(false);
    // Limpar timeout de blur se existir
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setSelectedProductServiceId('');
    setSearchTerm('');
    setShowSuggestions(false);
    setQuantity(1);
    setCustomUnitPrice(0);
  };

  const handleSaveItem = async () => {
    const productServiceId = editingItem
      ? selectedProductServiceId ||
        editingItem.productService?.id ||
        editingItem.productServiceId
      : selectedProductServiceId;

    if (!productServiceId || quantity <= 0) {
      showErrorNotification(
        'Selecione um produto/serviço e informe uma quantidade válida'
      );
      return;
    }

    // Buscar o produto completo para criar
    const selectedProduct = productServices.find(
      p => p.id === Number(productServiceId)
    );

    if (!selectedProduct && !editingItem) {
      showErrorNotification('Produto/serviço não encontrado');
      return;
    }

    const unitPriceValue =
      typeof customUnitPrice === 'string'
        ? parseFloat(customUnitPrice.replace(',', '.')) || 0
        : customUnitPrice;

    setIsSubmitting(true);
    try {
      let response;
      if (editingItem) {
        // Edição: enviar productServiceId, quantity e unitPrice
        response = await TicketBillableItemService.edit(
          ticketId,
          editingItem.id,
          {
            productServiceId: Number(productServiceId),
            quantity: quantity,
            unitPrice: unitPriceValue
          }
        );

        if (response.userMessage) {
          showErrorNotification(response.userMessage);
          setIsSubmitting(false);
          return;
        }

        showSuccessNotification('Item faturável editado com sucesso');
      } else {
        // Criação
        response = await TicketBillableItemService.create(ticketId, {
          productServiceId: selectedProduct.id,
          quantity: quantity,
          description: `${selectedProduct.name} - ${quantity} ${
            quantity === 1 ? 'unidade' : 'unidades'
          }`,
          unitPrice: unitPriceValue
        });

        if (response.userMessage) {
          showErrorNotification(response.userMessage);
          setIsSubmitting(false);
          return;
        }

        showSuccessNotification('Item faturável criado com sucesso');
      }

      // Limpar campos
      resetForm();

      getData();
      refreshLogs?.();
    } catch (error) {
      console.error('Erro ao salvar item:', error);
      showErrorNotification(
        editingItem
          ? 'Erro ao editar item faturável'
          : 'Erro ao criar item faturável'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const getBillingStatusBadge = status => {
    const variants = {
      PENDING: 'warning',
      INVOICED: 'success',
      COMPLETED: 'info',
      CANCELED: 'danger'
    };
    const labels = {
      PENDING: 'Pendente',
      INVOICED: 'Faturado',
      COMPLETED: 'Concluído',
      CANCELED: 'Cancelado'
    };
    return (
      <Badge bg={variants[status] || 'secondary'}>
        {labels[status] || status}
      </Badge>
    );
  };

  const columns = [
    {
      accessorKey: 'productService.name',
      header: 'Item',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { productService } = rowData.row.original;
        return <div>{productService?.name || '-'}</div>;
      }
    },
    {
      accessorKey: 'quantity',
      header: 'Quantidade',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { quantity } = rowData.row.original;
        return <div>{quantity}</div>;
      }
    },
    {
      accessorKey: 'unitPrice',
      header: 'Preço Unit.',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { unitPrice } = rowData.row.original;
        return <div>{formatCurrency(unitPrice)}</div>;
      }
    },
    {
      accessorKey: 'totalPrice',
      header: 'Preço Total',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { totalPrice } = rowData.row.original;
        return <div className="fw-bold">{formatCurrency(totalPrice)}</div>;
      }
    },
    {
      accessorKey: 'launchedAt',
      header: 'Data Lançamento',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { launchedAt } = rowData.row.original;
        return <div>{formatDateTime(launchedAt)}</div>;
      }
    },
    {
      accessorKey: 'billingStatus',
      header: 'Status',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { billingStatus } = rowData.row.original;
        return getBillingStatusBadge(billingStatus);
      }
    },
    {
      accessorKey: 'actions',
      header: 'Ações',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const item = rowData.row.original;
        const isDisabled =
          ticketStatus === 'COMPLETED' ||
          ticketStatus === 'CLOSED' ||
          ticketStatus?.deleted ||
          item.billingStatus !== 'PENDING';
        return (
          <ButtonGroup>
            <Button
              variant="link"
              size="sm"
              onClick={() => handleEdit(item)}
              disabled={isDisabled}
              title={
                isDisabled
                  ? 'Apenas itens pendentes podem ser editados'
                  : 'Editar item'
              }
            >
              <FontAwesomeIcon icon={faPen} size="sm" />
            </Button>
            <Button
              variant="link"
              size="sm"
              onClick={() => handleDelete(item)}
              disabled={isDisabled}
              className="text-danger"
              title={
                isDisabled
                  ? 'Apenas itens pendentes podem ser excluídos'
                  : 'Excluir item'
              }
            >
              <FontAwesomeIcon icon={faTrash} size="sm" />
            </Button>
          </ButtonGroup>
        );
      }
    }
  ];

  const table = useAdvanceTable({
    data: items.content,
    columns
  });

  const handleEdit = item => {
    setEditingItem(item);
    setQuantity(item.quantity);
    // Definir o productServiceId e o nome do produto
    const productId = item.productService?.id || item.productServiceId;
    const productName = item.productService?.name || '';
    setCustomUnitPrice(item.unitPrice || 0);
    if (productId) {
      setSelectedProductServiceId(String(productId));
      setSearchTerm(productName);
    } else {
      setSelectedProductServiceId('');
      setSearchTerm('');
    }
    setShowSuggestions(false);
  };

  const handleDelete = item => {
    setSelectedItem(item);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedItem(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedItem) return;

    try {
      const response = await TicketBillableItemService.delete(
        ticketId,
        selectedItem.id
      );
      if (response.status === 204 || !response.userMessage) {
        showSuccessNotification('Item faturável excluído com sucesso');
        getData();
        refreshLogs?.();
        handleCloseDeleteModal();
      } else {
        showErrorNotification(response.userMessage || 'Erro ao excluir');
      }
    } catch (error) {
      showErrorNotification('Erro ao excluir item faturável');
    }
  };

  const formatCurrency = value => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  // Calcular valor unitário do produto selecionado
  const getUnitPrice = () => {
    if (customUnitPrice !== undefined && customUnitPrice !== null) {
      if (typeof customUnitPrice === 'string') {
        return parseFloat(customUnitPrice.replace(',', '.')) || 0;
      }
      return customUnitPrice;
    }

    // Se estiver editando e não tiver produto selecionado no select, usar o valor do item
    if (editingItem && !selectedProductServiceId) {
      return editingItem.unitPrice || 0;
    }

    if (!selectedProductServiceId) return 0;
    const product = productServices.find(
      p => p.id === Number(selectedProductServiceId)
    );
    return product?.unitPrice || 0;
  };

  // Calcular valor total (quantidade * valor unitário)
  const getTotalPrice = () => {
    return quantity * getUnitPrice();
  };

  const calculateTotal = () => {
    return items.content.reduce((total, item) => {
      const isCanceled = item.billingStatus === 'CANCELED';
      if (!isCanceled) {
        return total + item.totalPrice;
      }
      return total;
    }, 0);
  };

  const calculateProductsTotal = () => {
    return items.content.reduce((total, item) => {
      const productType = item.productService?.type;
      const isCanceled = item.billingStatus === 'CANCELED';
      if (productType === 'PRODUCT' && !isCanceled) {
        return total + item.totalPrice;
      }
      return total;
    }, 0);
  };

  const calculateServicesTotal = () => {
    return items.content.reduce((total, item) => {
      const productType = item.productService?.type;
      const isCanceled = item.billingStatus === 'CANCELED';
      if (productType === 'SERVICE' && !isCanceled) {
        return total + item.totalPrice;
      }
      return total;
    }, 0);
  };

  const isDisabledForm =
    ticketStatus === 'COMPLETED' || ticketStatus === 'CLOSED' || ticketStatus?.deleted;

  return (
    <>
      <TicketBillableItemDeleteModal
        showModal={showDeleteModal}
        handleClose={handleCloseDeleteModal}
        handleConfirm={handleConfirmDelete}
        item={selectedItem}
      />

      <Flex justifyContent="between" alignItems="center" className="mb-4">
        <h3 className="m-0">Itens Faturáveis</h3>
      </Flex>

      {/* Formulário de criação/edição inline */}
      {!isDisabledForm && (
        <div className="mb-3 p-2 border rounded">
          <Row className="g-2 align-items-end">
            <Col xs={12} sm={editingItem ? 4 : 5}>
              <Form.Label className="mb-1 small fw-semibold">
                Produto/Serviço <span className="text-danger">*</span>
              </Form.Label>
              <div ref={searchInputContainerRef} className="position-relative">
                <SearchInputWithSuggestions
                  ref={searchInputRef}
                  searchTerm={searchTerm}
                  placeholder={
                    isLoadingProducts
                      ? 'Carregando produtos...'
                      : 'Digite para buscar produto/serviço...'
                  }
                  disabled={isSubmitting || isLoadingProducts}
                  suggestions={productServices}
                  showSuggestions={
                    showSuggestions &&
                    productServices.length > 0 &&
                    !isLoadingProducts
                  }
                  onSearchChange={handleSearchChange}
                  onSuggestionSelect={handleProductServiceSelect}
                  renderSuggestionItem={product => (
                    <div
                      key={product.id}
                      className="px-3 py-2 border-bottom cursor-pointer hover-bg-200"
                      onClick={() => handleProductServiceSelect(product)}
                      onMouseDown={e => {
                        // Prevenir que o blur seja disparado ao clicar na sugestão
                        e.preventDefault();
                      }}
                      role="button"
                      style={{
                        transition: 'background-color 0.2s'
                      }}
                    >
                      <div className="fw-bold">{product.name}</div>
                      <div className="text-muted small">
                        {product.type && (
                          <span className="me-2">
                            {product.type === 'SERVICE' ? 'Serviço' : 'Produto'}
                          </span>
                        )}
                        {product.description && (
                          <span>{product.description}</span>
                        )}
                      </div>
                    </div>
                  )}
                  size="sm"
                  required
                  inputProps={{
                    onFocus: () => {
                      if (!isLoadingProducts) {
                        setShowSuggestions(true);
                      }
                      // Limpar timeout de blur se existir
                      if (blurTimeoutRef.current) {
                        clearTimeout(blurTimeoutRef.current);
                        blurTimeoutRef.current = null;
                      }
                    },
                    onBlur: handleBlur
                  }}
                />
                {isLoadingProducts && (
                  <div className="position-absolute end-0 top-50 translate-middle-y me-2">
                    <Spinner animation="border" size="sm" variant="primary" />
                  </div>
                )}
              </div>
            </Col>
            <Col xs={6} sm={editingItem ? 2 : 2}>
              <Form.Label className="mb-1 small fw-semibold">
                Qtd <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="number"
                step="0.5"
                min="0.5"
                size="sm"
                value={quantity}
                onChange={e => {
                  const qty = parseFloat(e.target.value) || 1;
                  setQuantity(qty);
                }}
                disabled={
                  (!selectedProductServiceId && !editingItem) || isSubmitting
                }
              />
            </Col>
            <Col xs={6} sm={editingItem ? 2 : 2}>
              <Form.Label className="mb-1 small fw-semibold">
                Valor Unit.
              </Form.Label>
              <CurrencyInput
                id="unitPrice"
                name="unitPrice"
                placeholder="R$ 0,00"
                value={customUnitPrice}
                decimalsLimit={2}
                onValueChange={value => setCustomUnitPrice(value)}
                prefix="R$ "
                className="form-control form-control-sm"
                intlConfig={{ locale: 'pt-BR', currency: 'BRL' }}
                disabled={
                  (!selectedProductServiceId && !editingItem) || isSubmitting
                }
              />
            </Col>
            <Col xs={6} sm={editingItem ? 2 : 2}>
              <Form.Label className="mb-1 small fw-semibold">
                Valor Total
              </Form.Label>
              <Form.Control
                type="text"
                size="sm"
                value={formatCurrency(getTotalPrice())}
                disabled
                readOnly
                className="fw-bold text-primary"
              />
            </Col>
            <Col xs={6} sm={editingItem ? 2 : 1}>
              {editingItem ? (
                <div className="d-flex gap-1">
                  <Button
                    variant="falcon-default"
                    size="sm"
                    onClick={resetForm}
                    disabled={isSubmitting}
                    className="px-2"
                    title="Cancelar edição"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSaveItem}
                    disabled={
                      isSubmitting ||
                      (!selectedProductServiceId &&
                        !(
                          editingItem?.productService?.id ||
                          editingItem?.productServiceId
                        )) ||
                      quantity <= 0
                    }
                    title="Salvar alterações"
                    className="flex-fill d-flex align-items-center justify-content-center"
                  >
                    {isSubmitting ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faSave} className="me-2" />
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="mt-4">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSaveItem}
                    disabled={
                      isSubmitting || !selectedProductServiceId || quantity <= 0
                    }
                    className="w-100 d-flex align-items-center justify-content-center"
                    title="Adicionar item"
                  >
                    {isSubmitting ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <FontAwesomeIcon icon={faPlus} />
                    )}
                  </Button>
                </div>
              )}
            </Col>
          </Row>
        </div>
      )}
      {isLoading ? (
        <Flex justifyContent="center" className="py-4">
          <Spinner animation="border" variant="primary" />
        </Flex>
      ) : items.content.length === 0 ? (
        <EmptyList imgSize={300} vhSize={50} />
      ) : (
        <>
          <AdvanceTableProvider {...table}>
            <AdvanceTable
              headerClassName="text-nowrap align-middle"
              rowClassName="align-middle white-space-nowrap"
              tableProps={{
                striped: true,
                className: 'fs-10 mb-0 overflow-hidden'
              }}
            />
          </AdvanceTableProvider>

          {items.content.length > 0 && (
            <div className="mt-4 pt-3 border-top">
              <Flex justifyContent="end">
                <div className="d-flex align-items-center justify-content-end gap-4">
                  <div>
                    <span className="text-muted">Total Produtos </span>
                    <span className="fw-semibold">
                      {formatCurrency(calculateProductsTotal())}
                    </span>
                  </div>
                  <span className="fw-semibold">+</span>
                  <div>
                    <span className="text-muted">Total Serviços </span>
                    <span className="fw-semibold">
                      {formatCurrency(calculateServicesTotal())}
                    </span>
                  </div>
                  <div>
                    <span className="fw-semibold me-2">=</span>
                    <span className="text-muted">Total Geral </span>
                    <span className="fw-bold text-muted">
                      {formatCurrency(calculateTotal())}
                    </span>
                  </div>
                </div>
              </Flex>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default TicketBillableItems;
